import { state, prefs, getSelectedKeyframe, pushUndo, notify, PALETTE_SIZE, debouncedSave, snapshotKf, recordKfHistory } from './state.js';
import { showToast, showPromptModal, showConfirmModal } from './ui.js';
import { renderGrid } from './grid.js';

// ================================================================
// PALETTES PANEL
// ================================================================

function getActiveColor() {
  const colors = state.project?.activePalette?.colors;
  if (!colors) return null;
  return colors[state.ui.activeColorIndex] || null;
}

function setSlotColor(idx, hex) {
  pushUndo();
  state.project.activePalette.colors[idx] = hex || null;
  state.ui.activeColorIndex = idx;
  notify("paletteSlot");
  renderPalettesPanel();
}

function clearSlot(idx) {
  pushUndo();
  state.project.activePalette.colors[idx] = null;
  notify("paletteSlot");
  renderPalettesPanel();
}

function applyActiveToSelectedCell() {
  const color = getActiveColor();
  const kf = getSelectedKeyframe();
  if (!color || !kf || !state.ui.selectedCell) return false;
  pushUndo();
  const snap = snapshotKf(kf);
  kf.grid[state.ui.selectedCell.row][state.ui.selectedCell.col].color = color;
  notify("paletteApplyCell");
  recordKfHistory(kf, "cell color", snap);
  renderGrid();
  return true;
}

function applyActiveAsDefault() {
  const color = getActiveColor();
  const kf = getSelectedKeyframe();
  if (!color || !kf) return;
  pushUndo();
  const snap = snapshotKf(kf);
  kf.defaultColor = color;
  notify("paletteApplyDefault");
  recordKfHistory(kf, "default color", snap);
  renderGrid();
}

function applyActiveAsBackground() {
  const color = getActiveColor();
  const kf = getSelectedKeyframe();
  if (!color || !kf) return;
  pushUndo();
  const snap = snapshotKf(kf);
  kf.backgroundColor = color;
  notify("paletteApplyBg");
  recordKfHistory(kf, "background color", snap);
  renderGrid();
  renderPalettesPanel();
}

function clearKeyframeBackground() {
  const kf = getSelectedKeyframe();
  if (!kf) return;
  pushUndo();
  const snap = snapshotKf(kf);
  kf.backgroundColor = null;
  notify("paletteClearBg");
  recordKfHistory(kf, "background color", snap);
  renderGrid();
  renderPalettesPanel();
}

async function pickFromScreen() {
  if (!window.EyeDropper) {
    showToast("EyeDropper not supported in this browser");
    return;
  }
  try {
    const dropper = new window.EyeDropper();
    const result = await dropper.open();
    if (result && result.sRGBHex) {
      setSlotColor(state.ui.activeColorIndex, result.sRGBHex);
    }
  } catch (e) {
    // user cancelled — no toast
  }
}

function saveCurrentPalette() {
  const colors = state.project?.activePalette?.colors || [];
  if (!colors.some(c => c)) {
    showToast("Active palette is empty");
    return;
  }
  showPromptModal("Save palette as", "Palette " + (prefs.palettes.length + 1), (name) => {
    if (!name || !name.trim()) return;
    prefs.palettes.push({
      id: "pal_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6),
      name: name.trim(),
      colors: colors.slice(),
    });
    debouncedSave();
    showToast("Palette saved");
    renderPalettesPanel();
  });
}

function loadSavedPalette(palId) {
  const pal = prefs.palettes.find(p => p.id === palId);
  if (!pal) return;
  pushUndo();
  const next = pal.colors.slice(0, PALETTE_SIZE);
  while (next.length < PALETTE_SIZE) next.push(null);
  state.project.activePalette.colors = next;
  notify("paletteLoad");
  renderPalettesPanel();
}

function deleteSavedPalette(palId) {
  const pal = prefs.palettes.find(p => p.id === palId);
  if (!pal) return;
  showConfirmModal('Delete palette "' + pal.name + '"?', () => {
    prefs.palettes = prefs.palettes.filter(p => p.id !== palId);
    debouncedSave();
    renderPalettesPanel();
  });
}

// ================================================================
// RENDER
// ================================================================
export function renderPalettesPanel() {
  const container = document.getElementById("palettes-panel");
  if (!container) return;
  if (!state.project?.activePalette) return;

  const colors = state.project.activePalette.colors;
  const activeIdx = state.ui.activeColorIndex;
  const editingIdx = state.ui.editingPaletteSlot;
  const kf = getSelectedKeyframe();
  const hasActive = !!getActiveColor();
  const hasSelectedCell = !!state.ui.selectedCell;

  container.innerHTML = "";

  // Header
  const header = document.createElement("div");
  header.className = "palettes-header";
  header.textContent = "Palettes";
  container.appendChild(header);

  // ---- Active palette ----
  const activeSection = document.createElement("div");
  activeSection.className = "palette-section";

  const activeLabel = document.createElement("div");
  activeLabel.className = "palette-section-label";
  activeLabel.textContent = "Active";
  activeSection.appendChild(activeLabel);

  const swatchGrid = document.createElement("div");
  swatchGrid.className = "palette-swatch-grid";
  for (let i = 0; i < PALETTE_SIZE; i++) {
    const color = colors[i];
    const sw = document.createElement("button");
    sw.className = "palette-swatch";
    if (!color) sw.classList.add("empty");
    if (i === activeIdx) sw.classList.add("active");
    if (color) sw.style.backgroundColor = color;
    sw.title = color
      ? color + "  (click to use, right-click to edit)"
      : "Empty slot — right-click to set a color";
    sw.addEventListener("click", () => {
      state.ui.activeColorIndex = i;
      if (color && state.ui.selectedCell) {
        applyActiveToSelectedCell();
      }
      renderPalettesPanel();
    });
    sw.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      state.ui.activeColorIndex = i;
      state.ui.editingPaletteSlot = (editingIdx === i ? null : i);
      renderPalettesPanel();
    });
    swatchGrid.appendChild(sw);
  }
  activeSection.appendChild(swatchGrid);

  // Inline editor for the slot being edited
  if (editingIdx !== null && editingIdx >= 0 && editingIdx < PALETTE_SIZE) {
    const editor = document.createElement("div");
    editor.className = "palette-slot-editor";

    const currentHex = colors[editingIdx] || "#ffffff";

    const colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.value = currentHex;
    // Live preview without touching state (avoids polluting undo stack mid-drag)
    colorInput.addEventListener("input", () => {
      hexInput.value = colorInput.value;
      const swatches = swatchGrid.querySelectorAll(".palette-swatch");
      const sw = swatches[editingIdx];
      if (sw) {
        sw.classList.remove("empty");
        sw.style.backgroundColor = colorInput.value;
      }
    });
    // Commit once on close
    colorInput.addEventListener("change", () => {
      setSlotColor(editingIdx, colorInput.value);
    });
    editor.appendChild(colorInput);

    const hexInput = document.createElement("input");
    hexInput.type = "text";
    hexInput.className = "palette-hex-input";
    hexInput.value = currentHex;
    hexInput.maxLength = 7;
    hexInput.addEventListener("change", () => {
      const v = hexInput.value.trim();
      if (/^#[0-9a-fA-F]{6}$/.test(v)) {
        setSlotColor(editingIdx, v);
      } else {
        showToast("Use #rrggbb format");
        hexInput.value = colors[editingIdx] || "";
      }
    });
    editor.appendChild(hexInput);

    const clearBtn = document.createElement("button");
    clearBtn.className = "btn-sm";
    clearBtn.textContent = "Clear";
    clearBtn.addEventListener("click", () => {
      clearSlot(editingIdx);
    });
    editor.appendChild(clearBtn);

    const doneBtn = document.createElement("button");
    doneBtn.className = "btn-sm";
    doneBtn.textContent = "Done";
    doneBtn.addEventListener("click", () => {
      state.ui.editingPaletteSlot = null;
      renderPalettesPanel();
    });
    editor.appendChild(doneBtn);

    activeSection.appendChild(editor);
  }

  // Actions
  const actions = document.createElement("div");
  actions.className = "palette-actions";

  if (window.EyeDropper) {
    const pickBtn = document.createElement("button");
    pickBtn.className = "btn-sm";
    pickBtn.textContent = "\u{1F441} Pick";
    pickBtn.title = "Pick a color from anywhere on screen";
    pickBtn.addEventListener("click", pickFromScreen);
    actions.appendChild(pickBtn);
  }

  const applyBtn = document.createElement("button");
  applyBtn.className = "btn-sm";
  applyBtn.textContent = "Apply to cell";
  applyBtn.disabled = !hasActive || !hasSelectedCell;
  applyBtn.title = hasSelectedCell ? "Color the selected cell" : "Select a cell first";
  applyBtn.addEventListener("click", () => {
    if (!applyActiveToSelectedCell()) showToast("Select a cell first");
  });
  actions.appendChild(applyBtn);

  const defaultBtn = document.createElement("button");
  defaultBtn.className = "btn-sm";
  defaultBtn.textContent = "Set as default";
  defaultBtn.disabled = !hasActive || !kf;
  defaultBtn.title = "Use as the keyframe's default character color";
  defaultBtn.addEventListener("click", applyActiveAsDefault);
  actions.appendChild(defaultBtn);

  const bgBtn = document.createElement("button");
  bgBtn.className = "btn-sm";
  bgBtn.textContent = "Set as background";
  bgBtn.disabled = !hasActive || !kf;
  bgBtn.title = "Use as the background of this keyframe";
  bgBtn.addEventListener("click", applyActiveAsBackground);
  actions.appendChild(bgBtn);

  if (kf?.backgroundColor) {
    const clearBgBtn = document.createElement("button");
    clearBgBtn.className = "btn-sm";
    clearBgBtn.textContent = "Clear background";
    clearBgBtn.title = "Revert to project background";
    clearBgBtn.addEventListener("click", clearKeyframeBackground);
    actions.appendChild(clearBgBtn);
  }

  activeSection.appendChild(actions);
  container.appendChild(activeSection);

  // ---- Saved palettes library ----
  const savedSection = document.createElement("div");
  savedSection.className = "palette-section";

  const savedHeader = document.createElement("div");
  savedHeader.className = "palette-section-label palette-saved-header";

  const savedTitle = document.createElement("span");
  savedTitle.textContent = "Saved";
  savedHeader.appendChild(savedTitle);

  const saveBtn = document.createElement("button");
  saveBtn.className = "btn-sm";
  saveBtn.textContent = "Save current";
  saveBtn.addEventListener("click", saveCurrentPalette);
  savedHeader.appendChild(saveBtn);

  savedSection.appendChild(savedHeader);

  if (prefs.palettes.length === 0) {
    const empty = document.createElement("div");
    empty.className = "palette-empty-msg";
    empty.textContent = "No saved palettes yet.";
    savedSection.appendChild(empty);
  } else {
    const list = document.createElement("div");
    list.className = "palette-saved-list";
    for (const pal of prefs.palettes) {
      const row = document.createElement("div");
      row.className = "palette-saved-row";

      const name = document.createElement("div");
      name.className = "palette-saved-name";
      name.textContent = pal.name;
      row.appendChild(name);

      const mini = document.createElement("div");
      mini.className = "palette-mini-row";
      for (let i = 0; i < PALETTE_SIZE; i++) {
        const c = pal.colors[i];
        const m = document.createElement("span");
        m.className = "palette-mini" + (c ? "" : " empty");
        if (c) m.style.backgroundColor = c;
        mini.appendChild(m);
      }
      row.appendChild(mini);

      const rowActions = document.createElement("div");
      rowActions.className = "palette-saved-actions";

      const loadBtn = document.createElement("button");
      loadBtn.className = "btn-sm";
      loadBtn.textContent = "Load";
      loadBtn.addEventListener("click", () => loadSavedPalette(pal.id));
      rowActions.appendChild(loadBtn);

      const delBtn = document.createElement("button");
      delBtn.className = "btn-sm btn-danger-outline";
      delBtn.textContent = "\u00D7";
      delBtn.title = "Delete palette";
      delBtn.addEventListener("click", () => deleteSavedPalette(pal.id));
      rowActions.appendChild(delBtn);

      row.appendChild(rowActions);
      list.appendChild(row);
    }
    savedSection.appendChild(list);
  }

  container.appendChild(savedSection);
}
